import { createDomElementHandler } from "@/scripts/utils/dom.utils";
import { AbPc1Element } from "@/scripts/components/ab-pc-1/abPc1.element";
import { ProductCardViewModel } from "../variants/purchaseCta.types";
import { ProductSliderLayoutConfig } from "../abProducts.config";
import { resolveBreakpointValue } from "@/scripts/utils/breakpoints.util";

type AttributeReader = Pick<HTMLElement, "getAttribute" | "hasAttribute">;

/** Renders already-resolved card models through the existing ab-swiper element. */
export class ProductSliderRenderer {
  #rootEl?: HTMLElement;
  #cards: AbPc1Element[] = [];
  #layoutConfig: ProductSliderLayoutConfig;
  #layout: string;
  #listening = false;
  constructor(
    private readonly host: HTMLElement,
    private readonly attributes: AttributeReader,
    private readonly className = "ab-products",
  ) {
    this.#layout = this.attributes.getAttribute("data-layout") || "horizontal";
    this.#layoutConfig = { layout: this.#layout, breakpoints: [] };
  }

  setLayoutConfig(config: ProductSliderLayoutConfig) {
    this.#layoutConfig = config;
    if (config.breakpoints.length && !this.#listening) {
      window.addEventListener("resize", this.#onResize);
      this.#listening = true;
    } else if (!config.breakpoints.length) {
      this.#stopListening();
    }
    this.#updateLayout();
  }

  #onResize = () => this.#updateLayout();

  #updateLayout() {
    const layout = resolveBreakpointValue(this.#layoutConfig.breakpoints, window.innerWidth)
      ?? this.#layoutConfig.layout;
    if (layout === this.#layout) return;
    this.#layout = layout;
    this.#cards.forEach((card) => card.setAttribute("data-layout", layout));
  }

  #stopListening() {
    if (!this.#listening) return;
    window.removeEventListener("resize", this.#onResize);
    this.#listening = false;
  }

  setModels(models: readonly ProductCardViewModel[]) {
    this.#cards = [];
    this.#updateLayout();
    const rootEl = createDomElementHandler("div", this.className);
    if (!models.length) {
      rootEl.classList.add(`${this.className}--empty`);
      this.#replaceRoot(rootEl);
      return;
    }

    const swiperEl = createDomElementHandler("ab-swiper", `${this.className}__swiper`);
    this.#copySwiperAttributes(swiperEl);
    const containerEl = createDomElementHandler("div", "swiper");
    const wrapperEl = createDomElementHandler("div", "swiper-wrapper");
    const view = this.attributes.getAttribute("data-view");

    models.forEach((model) => {
      const slideEl = createDomElementHandler("div", "swiper-slide");
      const cardEl = createDomElementHandler("ab-pc-1") as AbPc1Element;
      cardEl.setAttribute("data-layout", this.#layout);
      this.#cards.push(cardEl);
      if (view?.length) cardEl.setAttribute("data-view", view);
      cardEl.cardModel = model;
      slideEl.appendChild(cardEl);
      wrapperEl.appendChild(slideEl);
    });

    containerEl.appendChild(wrapperEl);
    if (this.#isTruthyAttribute("navigation")) {
      containerEl.append(
        createDomElementHandler("div", "swiper-button-prev"),
        createDomElementHandler("div", "swiper-button-next"),
      );
    }
    if (this.#isTruthyAttribute("pagination")) {
      containerEl.appendChild(createDomElementHandler("div", "swiper-pagination"));
    }
    swiperEl.appendChild(containerEl);
    rootEl.appendChild(swiperEl);
    this.#replaceRoot(rootEl);
  }

  setLoading() {
    this.#setStatus("loading");
  }

  setError() {
    this.#setStatus("error");
  }

  destroy() {
    this.#stopListening();
    this.#cards = [];
    this.#rootEl?.remove();
    this.#rootEl = undefined;
  }

  #replaceRoot(rootEl: HTMLElement) {
    if (this.#rootEl) this.#rootEl.replaceWith(rootEl);
    else this.host.appendChild(rootEl);
    this.#rootEl = rootEl;
  }

  #setStatus(status: "loading" | "error") {
    this.#cards = [];
    const rootEl = createDomElementHandler("div", [this.className, `${this.className}--${status}`]);
    this.#replaceRoot(rootEl);
  }

  #copySwiperAttributes(swiperEl: HTMLElement) {
    const defaults: Record<string, string> = {
      "slides-per-view": "1.1",
      "space-between": "16",
      breakpoints: JSON.stringify({ 576: { slidesPerView: 1.25 }, 768: { slidesPerView: 1.5 }, 1024: { slidesPerView: 2 } }),
    };
    Object.entries(defaults).forEach(([name, value]) => {
      swiperEl.setAttribute(name, this.attributes.getAttribute(name) || value);
    });
    ["loop", "free-mode", "autoplay"].forEach((name) => {
      if (this.attributes.hasAttribute(name)) swiperEl.setAttribute(name, this.attributes.getAttribute(name) ?? "");
    });
  }

  #isTruthyAttribute(name: string) {
    const value = this.attributes.getAttribute(name);
    return value === "" || value === "true" || value === "1" || value === "yes";
  }
}
